import { useState } from "react";
import { CalendarDays, Users, MessageCircle, ShieldCheck, Clock, UtensilsCrossed } from "lucide-react";
import { Reveal } from "./Reveal";
import { ROOMS, waLink, PHONE_DISPLAY } from "../data";
import { useLang } from "../i18n";

const fmt = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export const Booking = () => {
  const { s } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    checkin: "",
    checkout: "",
    room: ROOMS[0].name,
    guests: "2",
    note: "",
  });
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.checkin || !form.checkout) {
      setError(s.booking.errors.required);
      return;
    }
    if (new Date(form.checkout) <= new Date(form.checkin)) {
      setError(s.booking.errors.dates);
      return;
    }
    setError("");
    const msg = [
      "Namaste! Booking enquiry — Shri Jamuna Dham, Vrindavan",
      "",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Check-in: ${fmt(form.checkin)}`,
      `Check-out: ${fmt(form.checkout)}`,
      `Room: ${form.room}`,
      `Guests: ${form.guests}`,
      form.note ? `Requests: ${form.note}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    window.open(waLink(msg), "_blank");
  };

  const inputCls =
    "w-full rounded-lg border border-cream/20 bg-cream/5 px-4 py-3.5 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors duration-300 focus:border-saffron";
  const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-gold";

  return (
    <section id="booking" className="relative bg-maroon py-24 sm:py-32 grain" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold" data-testid="booking-overline">
              {s.booking.overline}
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-cream tracking-tight" data-testid="booking-heading">
              {s.booking.heading}
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-cream/70">
              {s.booking.sub}
            </p>

            <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-form">
              <div>
                <label className={labelCls} htmlFor="bk-name">{s.booking.labels.name}</label>
                <input id="bk-name" data-testid="booking-name-input" className={inputCls} placeholder={s.booking.ph.name} value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-phone">{s.booking.labels.phone}</label>
                <input id="bk-phone" data-testid="booking-phone-input" className={inputCls} placeholder={s.booking.ph.phone} value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-checkin">{s.booking.labels.checkin}</label>
                <input id="bk-checkin" data-testid="booking-checkin-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.checkin} onChange={set("checkin")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-checkout">{s.booking.labels.checkout}</label>
                <input id="bk-checkout" data-testid="booking-checkout-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.checkout} onChange={set("checkout")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-room">{s.booking.labels.room}</label>
                <select id="bk-room" data-testid="booking-room-select" className={inputCls} value={form.room} onChange={set("room")}>
                  {ROOMS.map((r) => (
                    <option key={r.id} value={r.name} className="text-ink">
                      {r.name} — {s.roomData[r.id].type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-guests">{s.booking.labels.guests}</label>
                <select id="bk-guests" data-testid="booking-guests-select" className={inputCls} value={form.guests} onChange={set("guests")}>
                  {["1", "2", "3", "4", "5", "6+"].map((g) => (
                    <option key={g} value={g} className="text-ink">{g}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="bk-note">{s.booking.labels.note}</label>
                <textarea id="bk-note" data-testid="booking-note-input" rows={3} className={inputCls} placeholder={s.booking.ph.note} value={form.note} onChange={set("note")} />
              </div>

              {error && (
                <p className="sm:col-span-2 text-sm font-medium text-saffron" data-testid="booking-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="booking-submit-button"
                className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#1eb857]"
              >
                <MessageCircle size={18} /> {s.booking.submit}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="rounded-2xl border border-gold/20 bg-night/40 p-8 backdrop-blur-sm" data-testid="booking-direct-card">
              <h3 className="font-display text-2xl font-bold text-cream">{s.booking.direct.title}</h3>
              <ul className="mt-6 space-y-5 text-sm text-cream/80">
                {s.booking.direct.bullets.map((b, i) => {
                  const icons = [ShieldCheck, Clock, UtensilsCrossed, CalendarDays, Users];
                  const Icon = icons[i];
                  return (
                    <li key={i} className="flex gap-3">
                      <Icon size={18} className="shrink-0 text-saffron" />
                      {b}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-8 rounded-xl bg-cream/5 p-5">
                <p className="font-hindi text-xl text-gold">अतिथि देवो भवः</p>
                <p className="mt-1 text-xs text-cream/60">
                  {s.booking.direct.quoteSub}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
